import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
  Paper,
  Stack,
} from '@mui/material';
import {
  ArrowBack,
  Favorite,
  ShoppingCart,
  Lock,
  CheckCircle,
  LocalOffer,
} from '@mui/icons-material';
import { useHeart } from '../contexts/HeartContext';
import { apiClient } from '../config/api';
import { HeartPackage } from '../types';
import PurchaseConsentPopup from './Terms/PurchaseConsentPopup';
import { useSubmitPurchaseConsent, useActiveTerms } from '../hooks/useTerms';

// v3 정책 패키지 데이터
const PACKAGES: HeartPackage[] = [
  {
    id: 1,
    name: '스타터',
    heartAmount: 10,
    bonusAmount: 0,
    totalHearts: 10,
    price: 5000,
    discountRate: 0,
    pricePerHeart: 500,
  },
  {
    id: 2,
    name: '베이직',
    heartAmount: 30,
    bonusAmount: 0,
    totalHearts: 30,
    price: 14000,
    discountRate: 7,
    pricePerHeart: 467,
    isPopular: true,
  },
  {
    id: 3,
    name: '프리미엄',
    heartAmount: 100,
    bonusAmount: 0,
    totalHearts: 100,
    price: 39000,
    discountRate: 22,
    pricePerHeart: 390,
    isBestValue: true,
  },
];

const HeartPurchasePage: React.FC = () => {
  const navigate = useNavigate();
  const { balance, refreshBalance } = useHeart();
  const [packages, setPackages] = useState<HeartPackage[]>(PACKAGES);
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<HeartPackage | null>(null);
  // 구매 동의 팝업 상태
  const [consentOpen, setConsentOpen] = useState(false);
  const [pendingPackage, setPendingPackage] = useState<HeartPackage | null>(null);
  // 최초 구매 여부: 로컬스토리지에 기록 (서버 API 미구현 시 fallback)
  const [hasAgreedPurchaseConsent, setHasAgreedPurchaseConsent] = useState(
    () => localStorage.getItem('purchaseConsentAgreed') === 'true',
  );
  const { submit: submitConsent, loading: consentLoading } = useSubmitPurchaseConsent();
  const { terms: activeTerms } = useActiveTerms();

  // 페이지 진입 시 스크롤 최상단으로 이동
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 패키지 목록 조회 (서버에서 가져오기, 실패 시 로컬 데이터 사용)
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const serverPackages = await apiClient.getHeartPackages();
        if (serverPackages && serverPackages.length > 0) {
          setPackages(serverPackages);
        }
      } catch (error) {
        // 서버 조회 실패 시 로컬 PACKAGES 데이터 사용
      }
    };

    fetchPackages();
  }, []);

  const handlePurchase = (pkg: HeartPackage) => {
    setSelectedPackage(pkg);
    if (!hasAgreedPurchaseConsent) {
      // 최초 구매 — 청약철회 안내 팝업 표시
      setPendingPackage(pkg);
      setConsentOpen(true);
    } else {
      // 2회차 이후 — 바로 결제 진행
      executePurchase(pkg);
    }
  };

  const handleConsentConfirm = async () => {
    if (!pendingPackage) return;
    try {
      // 서버에 동의 기록 (실패해도 결제는 진행)
      const purchaseTermId = activeTerms.find((t) => t.type === 'PURCHASE_CONFIRM')?.id;
      if (purchaseTermId != null) {
        await submitConsent({ termsId: purchaseTermId }).catch(() => {});
      }
      localStorage.setItem('purchaseConsentAgreed', 'true');
      setHasAgreedPurchaseConsent(true);
      setConsentOpen(false);
      executePurchase(pendingPackage);
    } catch {
      setConsentOpen(false);
      executePurchase(pendingPackage);
    } finally {
      setPendingPackage(null);
    }
  };

  const executePurchase = (pkg: HeartPackage) => {
    // TODO: 실제 결제 화면으로 이동 또는 결제 모달 표시
    alert(`${pkg.name} 패키지 구매 화면으로 이동합니다.\n가격: ${pkg.price.toLocaleString()}원\n하트: ${pkg.totalHearts}개`);
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString('ko-KR');
  };

  const getOriginalPrice = (pkg: HeartPackage) => {
    if (pkg.discountRate === 0) return pkg.price;
    return Math.round(pkg.price / (1 - pkg.discountRate / 100));
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* 헤더 */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: '#0D1F3C' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, fontFamily: '"Playfair Display", serif' }}>
            하트 충전
          </Typography>
          <Chip
            icon={<Favorite sx={{ fontSize: 18 }} />}
            label={`${balance}개`}
            size="medium"
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 600,
            }}
          />
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* 현재 잔액 배너 */}
        <Box sx={{
          bgcolor: '#0D1F3C', color: 'white', borderRadius: 3,
          p: 3, mb: 3, textAlign: 'center'
        }}>
          <Typography sx={{ fontSize: 14, opacity: 0.8, mb: 1 }}>현재 보유 하트</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1 }}>
            {Array.from({ length: Math.min(balance, 10) }, (_, i) => (
              <Favorite key={i} sx={{ fontSize: 28, color: '#B87D8F' }} />
            ))}
          </Box>
          <Typography variant="h4" fontWeight={800} sx={{ color: '#C9A86C' }}>
            {balance}개
          </Typography>
        </Box>

        {/* 패키지 섹션 헤더 */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{
            fontFamily: '"Playfair Display", serif', fontWeight: 700,
            fontSize: 22, color: '#0D1F3C', mb: 0.5
          }}>
            하트 충전 패키지
          </Typography>
          <Box sx={{ width: 80, height: 2, bgcolor: '#C9A86C' }} />
        </Box>

        {/* 패키지 그리드 */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 2,
            mb: 4,
          }}
        >
          {packages.map((pkg) => (
            <Box key={pkg.id}>
              <Card
                sx={pkg.isPopular ? {
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '2px solid #C9A86C',
                  boxShadow: '0 8px 32px rgba(201,168,108,0.20)',
                  borderRadius: 3,
                  position: 'relative',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                } : {
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: pkg.isBestValue ? 2 : 0,
                  borderColor: pkg.isBestValue ? 'success.main' : 'transparent',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                {/* 뱃지 */}
                {(pkg.isPopular || pkg.isBestValue) && (
                  <Chip
                    label={pkg.isPopular ? '💖 인기' : '💎 최고 가치'}
                    color={pkg.isPopular ? 'secondary' : 'success'}
                    size="small"
                    sx={pkg.isPopular ? {
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: '#C9A86C',
                      color: '#0D1F3C',
                      fontWeight: 800,
                      fontSize: '0.8rem',
                    } : {
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      fontWeight: 700,
                      fontSize: '0.75rem',
                    }}
                  />
                )}

                <CardContent sx={{ flexGrow: 1, pt: pkg.isPopular || pkg.isBestValue ? 5 : 2 }}>
                  {/* 패키지명 */}
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {pkg.name}
                  </Typography>

                  {/* 하트 수량 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Favorite sx={{ color: '#D94F6E', fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="primary.main">
                      {pkg.heartAmount}
                    </Typography>
                    {pkg.bonusAmount > 0 && (
                      <Chip
                        label={`+${pkg.bonusAmount}`}
                        size="small"
                        color="warning"
                        sx={{ fontWeight: 700 }}
                      />
                    )}
                  </Box>

                  {/* 총 하트 */}
                  {pkg.bonusAmount > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      총 {pkg.totalHearts}개
                    </Typography>
                  )}

                  {/* 가격 */}
                  <Box sx={{ mb: 2 }}>
                    {pkg.discountRate > 0 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textDecoration: 'line-through' }}
                      >
                        ₩{formatPrice(getOriginalPrice(pkg))}
                      </Typography>
                    )}
                    <Typography variant="h6" fontWeight={700}>
                      ₩{formatPrice(pkg.price)}
                    </Typography>
                    {pkg.discountRate > 0 && (
                      <Chip
                        label={`🔥 ${pkg.discountRate}% 할인`}
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ mt: 0.5, fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>

                  {/* 개당 가격 */}
                  <Typography variant="caption" color="text.secondary">
                    ({Math.round(pkg.price / pkg.totalHearts)}원/하트)
                  </Typography>
                </CardContent>

                {/* 구매 버튼 */}
                <Box sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant={pkg.isPopular || pkg.isBestValue ? 'contained' : 'outlined'}
                    onClick={() => handlePurchase(pkg)}
                    startIcon={<ShoppingCart />}
                    sx={pkg.isPopular ? {
                      py: 1.5,
                      fontWeight: 700,
                      bgcolor: '#0D1F3C',
                      minHeight: 52,
                      '&:hover': { bgcolor: '#1A3461' },
                    } : {
                      py: 1.5,
                      fontWeight: 700,
                      minHeight: 52,
                      borderColor: '#0D1F3C',
                      color: '#0D1F3C',
                      '&:hover': { bgcolor: 'rgba(13,31,60,0.04)' },
                    }}
                  >
                    구매하기
                  </Button>
                </Box>
              </Card>
            </Box>
          ))}
        </Box>

        {/* 2회차 이후 인라인 환불 안내 */}
        {hasAgreedPurchaseConsent && (
          <Box sx={{ mb: 3, p: 2, bgcolor: '#FFF9F0', borderRadius: 2, border: '1px solid #F0D9A0' }}>
            <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary', lineHeight: 1.7 }}>
              💡 하트는 디지털 콘텐츠로, 사용 후에는 환불이 제한됩니다.
              미사용 하트는 구매일로부터 7일 이내 환불 가능합니다.
            </Typography>
          </Box>
        )}

        {/* 결제 보안 안내 */}
        <Paper sx={{ p: 3, bgcolor: '#F5F7FA', border: '1px solid #E2E8F0', borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Lock fontSize="small" />
            안전한 결제 시스템
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
              <Typography variant="body2">🔒 SSL 256bit 암호화</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
              <Typography variant="body2">✅ PG사 안전거래 인증</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
              <Typography variant="body2">📞 고객센터: 1588-XXXX</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
              <Typography variant="body2">💰 7일 이내 환불 가능 (미사용 시)</Typography>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* 최초 구매 시 청약철회 안내 팝업 */}
      {pendingPackage && (
        <PurchaseConsentPopup
          open={consentOpen}
          packageName={pendingPackage.name}
          price={pendingPackage.price}
          onConfirm={handleConsentConfirm}
          onCancel={() => { setConsentOpen(false); setPendingPackage(null); }}
          loading={consentLoading}
        />
      )}
    </Box>
  );
};

export default HeartPurchasePage;
